import { ref, reactive } from 'vue'
import { catalogService } from '@/services/catalogService'
import { cacheManager } from '@/services/cacheManager'
import { productMapper } from '@/services/productMapper'
import { toastService } from '@/services/toastService'

export function useCatalog() {
  const products = ref([])
  const currentProduct = ref(null)
  const isLoading = ref(false)
  const error = ref(null)
  const pagination = reactive({
    total: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0
  })

  /**
   * Fetch products from API with caching
   * @param {Object} params - Query parameters
   */
  const fetchProducts = async (params = {}) => {
    isLoading.value = true
    error.value = null

    try {
      // Bump the key when the mapped product shape changes so old entries
      // cannot hide fields added by the catalog API.
      const cacheKey = cacheManager.generateKey('products-v2', params)
      
      const cachedData = cacheManager.get(cacheKey)
      if (cachedData) {
        products.value = cachedData
        isLoading.value = false
        return
      }

      const data = await catalogService.getProducts({
        skip: params.skip || 0,
        take: params.take || 20,
        ...params
      })

      if (!Array.isArray(data)) {
        throw new Error('Invalid response format')
      }

      const mappedProducts = productMapper.mapProductsList(data)
      
      // Ordina i prodotti per createdAt in ordine ascendente
      mappedProducts.sort((a, b) => {
        if (!a.createdAt) return 1
        if (!b.createdAt) return -1
        return new Date(a.createdAt) - new Date(b.createdAt)
      })

      products.value = mappedProducts

      cacheManager.setWithDefaultTTL('productsList', cacheKey, mappedProducts)
    } catch (err) {
      console.error('[useCatalog] Error fetching products:', err)
      const errorMessage = err.message || 'Errore nel caricamento dei prodotti. Verificare la connessione alla API.'
      error.value = errorMessage
      products.value = []
      toastService.error(`Errore caricamento: ${errorMessage}`, 6000)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch single product by ID
   * @param {number} id - Product ID
   * @param {number} [variantId] - Optional variant ID
   */
  const fetchProductById = async (id, variantId) => {
    isLoading.value = true
    error.value = null

    try {
      if (!id) {
        throw new Error('Product ID is required')
      }

      const cacheKey = variantId 
        ? `product:${id}:variant:${variantId}`
        : `product:${id}`
      
      const cachedData = cacheManager.get(cacheKey)
      if (cachedData) {
        currentProduct.value = cachedData
        isLoading.value = false
        return
      }

      const data = await catalogService.getProductById(id, variantId)
      const mappedProduct = productMapper.mapSingleProduct(data)
      
      currentProduct.value = mappedProduct
      cacheManager.setWithDefaultTTL('productDetail', cacheKey, mappedProduct)
    } catch (err) {
      console.error('[useCatalog] Error fetching product:', err)
      const errorMessage = err.message || 'Errore nel caricamento del prodotto.'
      error.value = errorMessage
      currentProduct.value = null
      toastService.error(`Errore caricamento prodotto: ${errorMessage}`, 6000)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch filters (colors, sizes, prices)
   * @param {Object} params - Query parameters
   * @param {number} [params.categoryId]
   * @param {number} [params.categoryTypeId]
   * @returns {Promise<Object>}
   */
  const fetchFilters = async (params = {}) => {
    try {
      const cacheKey = `filters:${JSON.stringify(params)}` || 'filters'
      
      const cachedData = cacheManager.get(cacheKey)
      if (cachedData) {
        return cachedData
      }

      const data = await catalogService.getFilters(params)
      cacheManager.setWithDefaultTTL('filters', cacheKey, data)
      
      return data
    } catch (err) {
      console.error('[useCatalog] Error fetching filters:', err)
      throw err
    }
  }

  /**
   * Clear cache for products
   */
  const clearCache = () => {
    cacheManager.clearPattern('products')
  }

  /**
   * Clear all caches
   */
  const clearAllCache = () => {
    cacheManager.clearAll()
  }

  /**
   * Reset to initial state
   */
  const reset = () => {
    products.value = []
    currentProduct.value = null
    error.value = null
    isLoading.value = false
    pagination.page = 1
  }

  return {
    products,
    currentProduct,
    isLoading,
    error,
    pagination,
    fetchProducts,
    fetchProductById,
    fetchFilters,
    clearCache,
    clearAllCache,
    reset
  }
}
